<template>
  <div class="appt-body min-w-[1000px]">
    <div class="actual-times">
      <div class="w-100 border-bottom">
        <h5 class="text-bold section-title mb-1">
          Actual Times
          <i class="far fa-edit link ml-1" @click="toggleCrewModal" />
        </h5>
      </div>
      <div class="grid-row prework headings">
        <span>Technician</span>
        <span>Accepted</span>
        <span
          >Pre-Work
          <i
            :class="{ 'gray-arrow': isTravelTimesEmpty }"
            class="fas fa-arrow-right ml-2"
          />
        </span>
        <span>Travel Started</span>
        <span
          >Travel<i
            :class="{ 'gray-arrow': isTravelTimesEmpty }"
            class="fas fa-arrow-right ml-2"
        /></span>
        <span>Arrival</span>
        <span
          >Work<i
            :class="{ 'gray-arrow': isWorkTimesEmpty }"
            class="fas fa-arrow-right ml-2"
        /></span>
        <span>Departure</span>
        <span class="text-right">Duration</span>
      </div>
      <div class="grid-row prework data">
        <div class="tech-name-cont">
          <Link v-if="hasTech" :to="redirectToTech" class="d-flex">
            <tooltip :disabled="!isPinned" placement="top" class="full-width">
              <template #trigger>
                <truncate-text :expand="false">{{ techName }}</truncate-text>
              </template>
              <template #tooltip>
                <div><b>Pinned Reason: </b>{{ pinnedReason }}</div>
              </template>
            </tooltip>
            <tooltip
              v-if="techPhone"
              placement="top"
              :options="{ hideOnClick: true }"
              class="full-width phone-icon ml-1"
            >
              <template #trigger>
                <i class="far fa-phone" />
              </template>
              <template #tooltip>
                <div class="text-center">
                  {{ techPhone }}
                </div>
              </template>
            </tooltip>
          </Link>
          <span v-else>{{ techName }}</span>
        </div>
        <span>{{ formatTime(appointment.accepted_time) }}</span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="appointment.pre_work_minutes"
            default-text="No Accepted or Travel Started Set"
          ></minutes-label>
        </span>
        <span>{{ formatTime(appointment.dispatch_time) }}</span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="appointment.travel_minutes"
            default-text="No Arrival Time Set"
          ></minutes-label>
        </span>
        <span>{{ formatTime(appointment.arrival_time) }}</span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="appointment.work_minutes"
            default-text="No Departure Time Set"
          ></minutes-label>
        </span>
        <span>{{ formatTime(appointment.departure_time) }}</span>
        <span class="duration-cell"
          ><minutes-label
            class="duration-display"
            :minutes="appointment.duration_minutes"
            default-text="Appointment is Not Complete"
          ></minutes-label
        ></span>
      </div>
      <div v-if="hasCrewMembers" class="subheader headings">Crew Members</div>
      <div
        v-for="(crew, i) in appointment.crew_members"
        :key="`${crew.id}-${i}`"
        class="grid-row prework data"
      >
        <span class="d-flex mr-4">
          <truncate-text>{{ crew.full_name }}</truncate-text>
          <tooltip placement="top" :options="{ hideOnClick: true }">
            <template #trigger>
              <i class="far fa-phone ml-2" />
            </template>
            <template #tooltip>
              <div class="text-center">
                {{ crew.phone }}
              </div>
            </template>
          </tooltip></span
        >
        <span>{{ formatTime(crew.accepted_time) }}</span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="crew.pre_work_minutes"
            default-text="No Accepted or Travel Started Set"
          ></minutes-label>
        </span>
        <span>{{ formatTime(crew.dispatch_time) }}</span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="crew.travel_minutes"
            default-text="No Arrival Time Set"
          ></minutes-label>
        </span>
        <span>{{ formatTime(crew.arrival_time) }}</span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="crew.work_minutes"
            default-text="No Departure Time Set"
          ></minutes-label>
        </span>
        <span>{{ formatTime(crew.departure_time) }}</span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="crew.duration_minutes"
          ></minutes-label>
        </span>
      </div>
      <div v-if="showTotals" class="totals-grid prework">
        <span>Totals</span>
        <div></div>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="appointment.total_pre_work_minutes"
          ></minutes-label>
        </span>
        <span></span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="appointment.total_travel_minutes"
          ></minutes-label>
        </span>
        <span></span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="appointment.total_work_minutes"
          ></minutes-label>
        </span>
        <span></span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="appointment.total_duration_minutes"
          ></minutes-label>
        </span>
      </div>
    </div>
    <crew-edit-modal
      v-if="crewEditModal"
      :appointment="appointment"
      @hide-modal="toggleCrewModal"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { getProperty } from "@/lib/helpers.js";
import { isFalsy } from "@/lib/helpers.js";

import Link from "@/components/Link.vue";
import MinutesLabel from "@/components/MinutesLabel.vue";
import Tooltip from "@/components/Tooltip.vue";
import TruncateText from "@/components/TruncateText.vue";
import CrewEditModal from "@/Job/CrewEditModal.vue";

export default {
  components: {
    Tooltip,
    CrewEditModal,
    Link,
    MinutesLabel,
    TruncateText
  },
  props: {
    appointment: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      crewEditModal: false
    };
  },
  computed: {
    hasCrewMembers() {
      return this.appointment.crew_members.length;
    },
    showTotals() {
      const {
        total_travel_minutes,
        total_work_minutes,
        total_duration_minutes
      } = this.appointment;
      return (
        !isFalsy(total_travel_minutes) &&
        !isFalsy(total_work_minutes) &&
        !isFalsy(total_duration_minutes)
      );
    },
    hasTech() {
      return this.techName !== "--";
    },
    isPinned() {
      return this.appointment.assignment_pinned;
    },
    pinnedReason() {
      return this.appointment.pinned_reason || "N/A";
    },
    techName() {
      let name = getProperty(this.appointment.technician, "full_name") || "--";
      if (this.isPinned) {
        name += ` (Pinned)`;
      }
      return name;
    },
    techPhone() {
      return getProperty(this.appointment.technician, "phone") || "";
    },
    redirectToTech() {
      const { technician } = this.appointment;
      return technician ? this.technicianShowPath(technician.user_id) : "";
    },
    isTravelTimesEmpty() {
      const { travel_minutes, crew_members } = this.appointment;
      return (
        isFalsy(travel_minutes) &&
        crew_members.every((member) => isFalsy(member.travel_minutes))
      );
    },
    isWorkTimesEmpty() {
      const { work_minutes, crew_members } = this.appointment;
      return (
        isFalsy(work_minutes) &&
        crew_members.every((member) => isFalsy(member.work_minutes))
      );
    }
  },
  methods: {
    toggleCrewModal() {
      this.crewEditModal = !this.crewEditModal;
      if (!this.crewEditModal) {
        EventBus.$emit("refresh-job-notes");
      }
    },
    formatTime(time) {
      if (!time) {
        return "--";
      }
      return this.formatDateTimeWithTimezone(
        time,
        this.appointment.address.time_zone
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.actual-times {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: var(--light-color);
  width: 100%;
  padding: 12px;
  border-radius: 5px;
}

.appt-body {
  display: flex;
  justify-content: space-between;
}

.time-display {
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  align-items: flex-start;
}

.grid-headers > b > i {
  margin-right: 5px;
}

.grid-row {
  display: grid;
  width: 100%;
  padding: 0 8px;
  grid: 1fr / 15% repeat(5, 1fr) 7%;
  &.prework {
    grid: 1fr / 15% repeat(7, 1fr) 7%;
  }
  &.headings > span {
    font-weight: 500;
  }
  &.data > * {
    font-weight: 600;
  }
}

.subheader {
  width: 100%;
  margin-top: 1rem;
  padding-left: 8px;
  &.headings {
    font-weight: 500;
  }
}

.section-title {
  font-size: 1rem;
  padding-left: 8px;
}

.totals-grid {
  margin-top: 1rem;
  display: grid;
  width: 100%;
  grid: 1fr / 15% repeat(5, 1fr) 7%;
  padding: 8px;
  background-color: white;
  border: 1px solid var(--gray-border-color);
  border-radius: 8px;
  font-weight: bold;
  &.prework {
    grid: 1fr / 15% repeat(7, 1fr) 7%;
  }
}

.duration-cell {
  display: flex;
  text-align: end;
  align-items: center;
  i {
    margin-top: 4px;
  }
  .duration-display {
    width: 60px;
    white-space: nowrap;
  }
  &:last-child {
    justify-content: flex-end;
  }
}

.gray-arrow {
  color: var(--secondary-color);
}
.tech-name-cont {
  width: 80%;
}
.phone-icon {
  color: var(--font-color);
}
</style>
